import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Trophy, 
  Zap, 
  Calendar, 
  Clock, 
  Users, 
  Award, 
  Sparkles, 
  ArrowRight, 
  Layers, 
  CheckCircle2, 
  Flame, 
  Filter,
  DollarSign,
  DownloadCloud,
  Eye,
  Heart
} from 'lucide-react';
import { fetchCompetitions } from '../api/client';
import Button from '../components/Button';
import './Competitions.css';

// ── Countdown Helper ──
function getRemainingTime(targetDate) {
  if (!targetDate) return 'Ended';
  const diff = new Date(targetDate).getTime() - new Date().getTime();
  if (diff <= 0) return 'Ended';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);

  if (days > 0) return `${days}d ${hours}h remaining`;
  if (hours > 0) return `${hours}h ${minutes}m remaining`;
  return `${minutes}m remaining`;
}

export default function Competitions() {
  const navigate = useNavigate();
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const { data, isLoading, error } = useQuery({
    queryKey: ['competitions', selectedFormat, selectedStatus, selectedCategory],
    queryFn: () => fetchCompetitions({
      format_type: selectedFormat !== 'all' ? selectedFormat : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      category: selectedCategory !== 'All' ? selectedCategory : undefined,
    }),
  });

  // Rich sample challenges for instant, world-class presentation
  const mockCompetitions = [
    {
      id: 1,
      title: "FinTech 2030: Next-Gen Banking OS Interface",
      description: "Design an ultra-clean, spatial finance dashboard that redefines how high-net-worth investors track global liquidity, crypto assets, and AI automated trading portfolios.",
      format_type: "tournament",
      status: "active_submission",
      category: "UI / UX Design",
      prize_pool: "$3,500 Cash Bounty + Figma Lifetime",
      entry_count: 64,
      submission_deadline: new Date(Date.now() + 86400000 * 5).toISOString(),
      voting_deadline: new Date(Date.now() + 86400000 * 8).toISOString(),
      banner_url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&q=80",
      featured: true,
      sponsor: "Linear Finance Lab",
      tools: ["Figma", "Photoshop", "Spline"]
    },
    {
      id: 2,
      title: "Cyberpunk Hologram Brand Identity & Motion",
      description: "Craft a fictional quantum computing brand identity including holographic vector mark, dynamic kinetic typography reel, and interactive 3D poster series.",
      format_type: "blitz",
      status: "active_voting",
      category: "Motion & Branding",
      prize_pool: "$1,200 Cash Bounty",
      entry_count: 112,
      submission_deadline: new Date(Date.now() - 86400000).toISOString(),
      voting_deadline: new Date(Date.now() + 86400000 * 2).toISOString(),
      banner_url: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=1200&q=80",
      featured: false,
      sponsor: "Hyperion Tokyo",
      tools: ["After Effects", "Illustrator", "Blender"]
    },
    {
      id: 3,
      title: "Neo-Brutalist Mobile Commerce Experience",
      description: "Redesign the modern streetwear drops shopping flow with bold typography, tactile micro-haptics, high-contrast layouts, and seamless 1-tap checkout.",
      format_type: "weekly",
      status: "active_submission",
      category: "Mobile Design",
      prize_pool: "$850 Cash + Pro Verified Badge",
      entry_count: 48,
      submission_deadline: new Date(Date.now() + 86400000 * 3).toISOString(),
      voting_deadline: new Date(Date.now() + 86400000 * 6).toISOString(),
      banner_url: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=1200&q=80",
      featured: false,
      sponsor: "HypeCraft Studio",
      tools: ["Figma", "Protopie"]
    },
    {
      id: 4,
      title: "Abstract 3D Procedural Packaging Series",
      description: "Produce photorealistic luxury perfume bottle 3D packaging renders with complex procedural glass dispersion, liquid shaders, and foil embossed label typography.",
      format_type: "weekly",
      status: "completed",
      category: "3D & Industrial",
      prize_pool: "$1,500 Cash Bounty",
      entry_count: 95,
      submission_deadline: new Date(Date.now() - 86400000 * 10).toISOString(),
      voting_deadline: new Date(Date.now() - 86400000 * 7).toISOString(),
      banner_url: "https://images.unsplash.com/photo-1600132806370-bf17e65e942f?w=1200&q=80",
      featured: false,
      sponsor: "Aura Fragrances",
      tools: ["Blender", "Cinema 4D", "Photoshop"]
    }
  ];

  const fetchedCompetitions = data?.data && data.data.length > 0 ? data.data : mockCompetitions;

  // Filter list
  const filteredList = fetchedCompetitions.filter(item => {
    const matchesFormat = selectedFormat === 'all' || item.format_type === selectedFormat;
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    const matchesCat = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesFormat && matchesStatus && matchesCat;
  });

  const featuredComp = fetchedCompetitions.find(c => c.featured) || fetchedCompetitions[0];

  return (
    <div className="competitions-page page-container" style={{ paddingBottom: '100px', paddingTop: '28px' }}>
      
      {/* ── Featured Hero Spotlight Banner ── */}
      {featuredComp && (
        <div className="comp-hero-spotlight interactive-card">
          <div className="comp-hero-backdrop" style={{ backgroundImage: `url(${featuredComp.banner_url})` }} />
          <div className="comp-hero-overlay" />
          
          <div className="comp-hero-content">
            <div className="comp-hero-badge-row">
              <span className="comp-tag-pill tournament">
                <Trophy size={14} /> Grand Prix Championship
              </span>
              <span className="comp-live-pill">
                <span className="live-pulse-dot" />
                {featuredComp.status === 'active_voting' ? 'COMMUNITY VOTING ACTIVE' : 'SUBMISSIONS OPEN'}
              </span>
              {featuredComp.sponsor && (
                <span className="comp-sponsor-pill">
                  Presented by <strong>{featuredComp.sponsor}</strong>
                </span>
              )}
            </div>

            <h1 className="comp-hero-title">{featuredComp.title}</h1>
            <p className="comp-hero-desc">{featuredComp.description}</p>

            <div className="comp-hero-meta-row">
              <div className="comp-hero-stat">
                <span className="stat-label">Prize Bounty</span>
                <strong className="stat-val prize">{featuredComp.prize_pool}</strong>
              </div>

              <div className="comp-hero-stat">
                <span className="stat-label">Timeline</span>
                <strong className="stat-val">
                  <Clock size={15} /> {getRemainingTime(featuredComp.submission_deadline)}
                </strong>
              </div>

              <div className="comp-hero-stat">
                <span className="stat-label">Contenders</span>
                <strong className="stat-val">
                  <Users size={15} /> {featuredComp.entry_count} Designers Enrolled
                </strong>
              </div>

              <div className="comp-hero-cta">
                <Button variant="primary" size="lg" onClick={() => navigate(`/competitions/${featuredComp.id}`)}>
                  View Challenge Brief & Submit <ArrowRight size={18} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Filter Controls & Navigation Bar ── */}
      <div className="comp-filters-bar">
        {/* Formats Tabs */}
        <div className="comp-format-tabs">
          {[
            { id: 'all', label: 'All Challenges', icon: Layers },
            { id: 'tournament', label: 'Championship Bounties', icon: Trophy },
            { id: 'blitz', label: '24-Hour Blitz Sprints', icon: Zap },
            { id: 'weekly', label: 'Weekly Creative Jams', icon: Calendar }
          ].map(fmt => (
            <button
              key={fmt.id}
              onClick={() => setSelectedFormat(fmt.id)}
              className={`comp-format-btn tactile-btn ${selectedFormat === fmt.id ? 'active' : ''}`}
            >
              <fmt.icon size={15} />
              <span>{fmt.label}</span>
            </button>
          ))}
        </div>

        {/* Status Dropdown */}
        <div className="comp-status-filter">
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Status:</span>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="comp-select-dropdown"
          >
            <option value="all">All Stages</option>
            <option value="active_submission">Open for Submission</option>
            <option value="active_voting">Community Voting</option>
            <option value="completed">Past Winners Hall of Fame</option>
          </select>
        </div>
      </div>

      {/* ── Challenge Cards Grid ── */}
      <div className="comp-grid">
        {filteredList.map(comp => (
          <div key={comp.id} className="comp-card interactive-card">
            {/* Card Banner */}
            <div className="comp-card-media">
              <img src={comp.banner_url} alt={comp.title} />
              
              <div className="comp-card-badges">
                <span className={`comp-tag-pill ${comp.format_type}`}>
                  {comp.format_type === 'blitz' ? '24h Sprint' : comp.format_type === 'tournament' ? 'Championship' : 'Weekly Jam'}
                </span>
                <span className="comp-status-badge">
                  {comp.status === 'active_voting' ? 'Live Voting' : comp.status === 'active_submission' ? 'Accepting Entries' : 'Concluded'}
                </span>
              </div>
            </div>

            {/* Card Body */}
            <div className="comp-card-body">
              <div className="comp-card-category">{comp.category}</div>
              <h3 className="comp-card-title">
                <Link to={`/competitions/${comp.id}`}>{comp.title}</Link>
              </h3>
              <p className="comp-card-desc">{comp.description}</p>

              {/* Tools row */}
              {comp.tools && (
                <div className="comp-card-tools">
                  {comp.tools.map(tool => (
                    <span key={tool} className="tool-chip">{tool}</span>
                  ))}
                </div>
              )}

              {/* Card Footer */}
              <div className="comp-card-footer">
                <div>
                  <div className="card-prize-label">Total Bounty</div>
                  <div className="card-prize-val">{comp.prize_pool}</div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div className="card-timer-label">Time Remaining</div>
                  <div className="card-timer-val">
                    <Clock size={13} /> {getRemainingTime(comp.submission_deadline)}
                  </div>
                </div>
              </div>

              <div className="comp-card-action">
                <Button variant="secondary" style={{ width: '100%' }} onClick={() => navigate(`/competitions/${comp.id}`)}>
                  Explore Brief & Submissions <ArrowRight size={15} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Creative Integrity & Jury Standards ── */}
      <div className="comp-integrity-banner">
        <div className="integrity-content">
          <div className="integrity-eyebrow">
            <Sparkles size={14} /> JURY & CURATION EXCELLENCE
          </div>
          <h2>How Buildive Challenges Are Evaluated</h2>
          <p>
            Winners are selected through a transparent dual-review framework combining verified community votes and expert Art Director jury reviews evaluated across four distinct design vectors:
          </p>

          <div className="integrity-pillars-grid">
            <div className="integrity-pillar">
              <CheckCircle2 size={18} color="var(--accent)" />
              <div>
                <strong>Visual Hierarchy & Craft (30%)</strong>
                <p>Clarity of focal points, spatial balance, and layout composition.</p>
              </div>
            </div>

            <div className="integrity-pillar">
              <CheckCircle2 size={18} color="var(--accent)" />
              <div>
                <strong>Typography & Color Theory (30%)</strong>
                <p>Strict typographic scale, contrast compliance, and palette harmony.</p>
              </div>
            </div>

            <div className="integrity-pillar">
              <CheckCircle2 size={18} color="var(--accent)" />
              <div>
                <strong>Originality & Concept (20%)</strong>
                <p>Fresh thinking, bold visual voice, and problem-solving ingenuity.</p>
              </div>
            </div>

            <div className="integrity-pillar">
              <CheckCircle2 size={18} color="var(--accent)" />
              <div>
                <strong>Source Layer Hygiene (20%)</strong>
                <p>Organized auto-layout frames, smart objects, and clean file structures.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
